import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

const SCROLL_EDGE_EPS = 2;

export type ReviewHorizontalCarouselProps = {
  children: React.ReactNode;
  contentKey: string | number;
};

const ReviewHorizontalCarousel: React.FC<ReviewHorizontalCarouselProps> = ({
  children,
  contentKey,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hovered, setHovered] = useState(false);

  const updateScrollEdges = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_EPS);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_EPS);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    updateScrollEdges();

    el.addEventListener("scroll", updateScrollEdges, { passive: true });
    const ro = new ResizeObserver(() => updateScrollEdges());
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollEdges);
      ro.disconnect();
    };
  }, [updateScrollEdges, contentKey]);

  const scrollByPage = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.max(120, Math.floor(el.clientWidth * 0.65)) * dir;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const showLeft = hovered && canScrollLeft;
  const showRight = hovered && canScrollRight;

  const arrowBtnSx = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
    color: "text.secondary",
    boxShadow: 1,
    transition: "opacity 0.2s ease, visibility 0.2s ease",
    "&:hover": {
      bgcolor: "action.hover",
    },
  };

  const arrowOpacitySx = (visible: boolean) => ({
    opacity: visible ? 1 : 0,
    visibility: visible ? ("visible" as const) : ("hidden" as const),
    pointerEvents: visible ? ("auto" as const) : ("none" as const),
  });

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: "relative", minWidth: 0 }}
    >
      <IconButton
        type="button"
        aria-label="See previous items"
        onClick={(e) => {
          e.stopPropagation();
          scrollByPage(-1);
        }}
        sx={{
          ...arrowBtnSx,
          left: 4,
          ...arrowOpacitySx(showLeft),
        }}
        size="small"
      >
        <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Box
        ref={scrollRef}
        sx={{
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          pb: 0.35,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}
      </Box>

      <IconButton
        type="button"
        aria-label="See more items"
        onClick={(e) => {
          e.stopPropagation();
          scrollByPage(1);
        }}
        sx={{
          ...arrowBtnSx,
          right: 4,
          ...arrowOpacitySx(showRight),
        }}
        size="small"
      >
        <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

export default ReviewHorizontalCarousel;
